import * as ContextMenuPrimitive from '@radix-ui/react-context-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';
import * as React from 'react';

import { cn } from '@heidi/utils';

import { useIsExperimentalDesignSystemForms } from '../hooks/use-is-experimental-design-system-forms';
import { menuItemVariants } from '../styles';

import {
  contextMenuCheckItemClassesExperimental,
  contextMenuLabelClassesExperimental,
  contextMenuSeparatorClassesExperimental,
  contextMenuSubTriggerClassesExperimental,
} from './context-menu.experimental.styles';
import {
  contextMenuCheckboxItemClasses,
  contextMenuContentClasses,
  contextMenuItemBaseStyles,
  contextMenuItemClasses,
  contextMenuItemDestructiveClasses,
  contextMenuLabelClasses,
  contextMenuRadioItemClasses,
  contextMenuSeparatorClasses,
  contextMenuShortcutClasses,
  contextMenuSubContentClasses,
  contextMenuSubTriggerClasses,
} from './context-menu.styles';
import type {
  ContextMenuCheckboxItemProps,
  ContextMenuContentProps,
  ContextMenuItemProps,
  ContextMenuLabelProps,
  ContextMenuRadioItemProps,
  ContextMenuSeparatorProps,
  ContextMenuShortcutProps,
  ContextMenuSubContentProps,
  ContextMenuSubTriggerProps,
} from './context-menu.types';

const ContextMenu = ContextMenuPrimitive.Root;

const ContextMenuTrigger = ContextMenuPrimitive.Trigger;

const ContextMenuGroup = ContextMenuPrimitive.Group;

const ContextMenuPortal = ContextMenuPrimitive.Portal;

const ContextMenuSub = ContextMenuPrimitive.Sub;

const ContextMenuRadioGroup = ContextMenuPrimitive.RadioGroup;

const ContextMenuSubTrigger = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.SubTrigger>,
  ContextMenuSubTriggerProps
>(({ className, inset, children, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <ContextMenuPrimitive.SubTrigger
      ref={ref}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? contextMenuSubTriggerClassesExperimental
          : cn(contextMenuItemBaseStyles, contextMenuSubTriggerClasses),
        inset && 'ps-8',
        className
      )}
      {...props}
    >
      {children}
      <ChevronRight className="ms-auto rtl:-scale-x-100" />
    </ContextMenuPrimitive.SubTrigger>
  );
});
ContextMenuSubTrigger.displayName = ContextMenuPrimitive.SubTrigger.displayName;
const ContextMenuSubContent = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.SubContent>,
  ContextMenuSubContentProps
>(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.SubContent
    ref={ref}
    className={cn(contextMenuSubContentClasses, className)}
    {...props}
  />
));
ContextMenuSubContent.displayName = ContextMenuPrimitive.SubContent.displayName;

const ContextMenuContent = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Content>,
  ContextMenuContentProps
>(({ className, ...props }, ref) => (
  <ContextMenuPrimitive.Portal>
    <ContextMenuPrimitive.Content
      ref={ref}
      className={cn(contextMenuContentClasses, className)}
      {...props}
    />
  </ContextMenuPrimitive.Portal>
));
ContextMenuContent.displayName = ContextMenuPrimitive.Content.displayName;

const ContextMenuItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Item>,
  ContextMenuItemProps
>(({ className, inset, variant, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <ContextMenuPrimitive.Item
      ref={ref}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? menuItemVariants({
              variant: variant === 'destructive' ? 'destructive' : undefined,
            })
          : cn(
              contextMenuItemBaseStyles,
              contextMenuItemClasses,
              variant === 'destructive' && contextMenuItemDestructiveClasses
            ),
        inset && 'ps-8',
        className
      )}
      {...props}
    />
  );
});
ContextMenuItem.displayName = ContextMenuPrimitive.Item.displayName;

const ContextMenuCheckboxItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.CheckboxItem>,
  ContextMenuCheckboxItemProps
>(({ className, children, checked, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <ContextMenuPrimitive.CheckboxItem
      ref={ref}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? contextMenuCheckItemClassesExperimental
          : cn(contextMenuItemBaseStyles, contextMenuCheckboxItemClasses),
        className
      )}
      checked={checked}
      {...props}
    >
      <span className="absolute start-2 flex size-3.5 items-center justify-center">
        <ContextMenuPrimitive.ItemIndicator>
          <Check className="size-4" />
        </ContextMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </ContextMenuPrimitive.CheckboxItem>
  );
});
ContextMenuCheckboxItem.displayName = ContextMenuPrimitive.CheckboxItem.displayName;

const ContextMenuRadioItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.RadioItem>,
  ContextMenuRadioItemProps
>(({ className, children, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <ContextMenuPrimitive.RadioItem
      ref={ref}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? contextMenuCheckItemClassesExperimental
          : cn(contextMenuItemBaseStyles, contextMenuRadioItemClasses),
        className
      )}
      {...props}
    >
      <span className="absolute start-2 flex size-3.5 items-center justify-center">
        <ContextMenuPrimitive.ItemIndicator>
          {isExperimentalForms ? (
            <Check className="size-4" />
          ) : (
            <Circle className="size-2 fill-current" />
          )}
        </ContextMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </ContextMenuPrimitive.RadioItem>
  );
});
ContextMenuRadioItem.displayName = ContextMenuPrimitive.RadioItem.displayName;

const ContextMenuLabel = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Label>,
  ContextMenuLabelProps
>(({ className, inset, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <ContextMenuPrimitive.Label
      ref={ref}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? contextMenuLabelClassesExperimental
          : contextMenuLabelClasses,
        inset && 'ps-8',
        className
      )}
      {...props}
    />
  );
});
ContextMenuLabel.displayName = ContextMenuPrimitive.Label.displayName;

const ContextMenuSeparator = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Separator>,
  ContextMenuSeparatorProps
>(({ className, ...props }, ref) => {
  const isExperimentalForms = useIsExperimentalDesignSystemForms();
  return (
    <ContextMenuPrimitive.Separator
      ref={ref}
      data-experimental={isExperimentalForms || undefined}
      className={cn(
        isExperimentalForms
          ? contextMenuSeparatorClassesExperimental
          : contextMenuSeparatorClasses,
        className
      )}
      {...props}
    />
  );
});
ContextMenuSeparator.displayName = ContextMenuPrimitive.Separator.displayName;

const ContextMenuShortcut = ({ className, ...props }: ContextMenuShortcutProps) => {
  return <span className={cn(contextMenuShortcutClasses, className)} {...props} />;
};
ContextMenuShortcut.displayName = 'ContextMenuShortcut';

export {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuPortal,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
};
